import { useState, useEffect, useContext } from 'react'
import { Link } from 'react-router-dom'
import { ButtonGroup, Button, Box, Dialog, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import QrCode2Icon from '@mui/icons-material/QrCode2'

import { AppContext } from '/providers/app-context'
import Templates from '/components/new-template/template-list'
import SelectedTemplateDialog from '/components/new-template/selected-template-dialog'

const CreateNewTemplate = ({ open: modalOpen, onClose, onCreated }) => {
  const { client, systemTemplates } = useContext(AppContext)
  const [templates, setTemplates] = useState([])
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [open, setOpen] = useState(false)
  const [tabIndex, setTabIndex] = useState('managed')

  useEffect(() => {
    const visibleTemplates = (Array.isArray(systemTemplates) ? systemTemplates : [])
      .filter(template => Number(client?.client_id) === 0 || template.specialist_template !== 1)
      .map(template => {
        const nextTemplate = { ...template }
        nextTemplate.apple_skeleton = nextTemplate.apple_pass_type
        if (nextTemplate.apple_sub_type && !/PKTransit/.test(nextTemplate.apple_sub_type)) {
          nextTemplate.apple_skeleton += '-' + nextTemplate.apple_sub_type.toLowerCase()
        }
        return nextTemplate
      })
    setTemplates(visibleTemplates)
  }, [systemTemplates, client?.client_id])

  useEffect(() => setOpen(selectedTemplate !== null), [selectedTemplate])
  useEffect(() => {
    if (!open) setSelectedTemplate(null)
  }, [open])

  const Menu = () => (
    <div className="flex flex-row justify-end whitespace-nowrap">
      <ButtonGroup variant="text" aria-label="Actions">
        <Button onClick={() => setTabIndex('managed')} sx={{ color: tabIndex === 'managed' ? 'link' : 'text.secondary' }}>Managed Templates</Button>
        <Button onClick={() => setTabIndex('custom')} sx={{ color: tabIndex === 'custom' ? 'link' : 'text.secondary' }}>Custom Templates</Button>
        {Number(client?.client_id) === 0 && <Button onClick={() => setTabIndex('specialist')} sx={{ color: tabIndex === 'specialist' ? 'link' : 'text.secondary' }}>Specialist Templates</Button>}
        <Button onClick={() => setTabIndex('examples')} sx={{ color: tabIndex === 'examples' ? 'link' : 'text.secondary' }}>Example Templates</Button>
      </ButtonGroup>
    </div>
  )

  const content = (
    <>
      {!selectedTemplate && <>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', px: 3, pt: 2, pr: 10 }}><Menu /></Box>
        <div className="mb-5 p-5"><TemplateTypeInfo tabIndex={tabIndex} /></div>
        <div className="flex flex-wrap w-full gap-5 max-w-full mt-10 justify-center h-full pb-96 content-start dashboard">
          <Templates templates={templates} tabIndex={tabIndex} selectedTemplate={selectedTemplate} setSelectedTemplate={setSelectedTemplate} />
        </div>
      </>}
      <SelectedTemplateDialog open={open} setOpen={setOpen} template={selectedTemplate} onCreated={onCreated} inline />
    </>
  )

  return (
    <Dialog open={Boolean(modalOpen)} fullWidth maxWidth="xl" onClose={onClose} PaperProps={{ sx: { height: '90vh', maxHeight: '90vh', backgroundColor: 'background.default' } }}>
      <Box sx={{ position: 'relative', height: '100%', overflow: 'auto' }}>
        <IconButton onClick={onClose} aria-label="Close" sx={{ position: 'absolute', right: 16, top: 12, zIndex: 1 }}><CloseIcon /></IconButton>
        {content}
      </Box>
    </Dialog>
  )
}

export default CreateNewTemplate

const TemplateTypeInfo = ({ tabIndex }) => {
  if (tabIndex === 'custom') return <><p>These templates give you full control over the content. You will be responsible for generating your own barcodes and implementing your own scanning.</p><p>Images below show example configurations.</p></>
  if (tabIndex === 'managed') return <><p>These templates can be used with the Smartix Scanner App. Smartix generates barcodes and handles scanning.</p><p>You have more limited editing opportunities because Smartix controls some content and layout.</p></>
  if (tabIndex === 'specialist') return <><p>These templates are not for general use. You may browse them here, but cannot select one.</p><p>If you require one, please <Link to="/support" className="link">get in touch</Link>.</p></>
  return <><p>These are example templates made with custom and managed templates. Use them for inspiration.</p><p>Note: Managed templates show the <QrCode2Icon /> icon.</p></>
}
